/**
 * <sale-countdown>
 *
 * Counts down to a target timestamp and swaps to an "expired" message when it passes.
 *
 * Liquid renders the target as a UTC epoch (seconds) so no timezone parsing happens
 * in the browser. Shopify caches storefront HTML on its CDN, which means Liquid's
 * `now` can be stale by hours — so the clock here is client-authoritative.
 *
 * To survive a visitor whose device clock is wrong, we take server time from the
 * `Date` response header of /cart.js (per-session, not CDN-cached) and keep the
 * offset against Date.now(). If that request fails — it does on a password-protected
 * storefront, where /cart.js redirects — we fall back to the device clock.
 *
 * Attributes:
 *   data-target           Target time, UTC epoch seconds (required)
 *   data-expired-target   Optional selector to reveal once the countdown expires
 *   data-reload-on-expiry "true" to reload the page when the countdown hits zero
 */
class SaleCountdown extends HTMLElement {
  constructor() {
    super();

    this.target = parseInt(this.dataset.target, 10) * 1000;
    this.clockOffset = 0;
    this.timer = null;
    this.expired = false;

    this.units = ['days', 'hours', 'minutes', 'seconds'].reduce((map, unit) => {
      map[unit] = this.querySelector(`[data-countdown-${unit}]`);
      return map;
    }, {});

    this.display = this.querySelector('[data-countdown-display]');
    this.expiredMessage = this.querySelector('[data-countdown-expired]');
    this.announcer = this.querySelector('[data-countdown-announcer]');
    this.lastAnnouncedMinute = null;
  }

  connectedCallback() {
    if (!this.target || Number.isNaN(this.target)) return;

    // Render immediately off the device clock so there's no blank frame, then
    // correct once we know real server time.
    this.tick();
    this.syncClock().finally(() => this.start());
  }

  disconnectedCallback() {
    this.stop();
  }

  /**
   * Reads server time from the Date header on /cart.js and stores the drift
   * between it and the device clock. Silent no-op on failure.
   */
  syncClock() {
    return fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      method: 'HEAD',
      cache: 'no-store',
    })
      .then((response) => {
        const serverDate = response.headers.get('Date');
        if (!serverDate) return;

        const serverTime = Date.parse(serverDate);
        if (Number.isNaN(serverTime)) return;

        this.clockOffset = serverTime - Date.now();
      })
      .catch(() => {
        /* Password-protected storefronts redirect /cart.js — device clock it is. */
      });
  }

  now() {
    return Date.now() + this.clockOffset;
  }

  start() {
    this.stop();
    this.tick();
    if (!this.expired) this.timer = setInterval(() => this.tick(), 1000);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  tick() {
    const remaining = this.target - this.now();

    if (remaining <= 0) {
      this.onExpired();
      return;
    }

    const seconds = Math.floor(remaining / 1000);
    const parts = {
      days: Math.floor(seconds / 86400),
      hours: Math.floor((seconds % 86400) / 3600),
      minutes: Math.floor((seconds % 3600) / 60),
      seconds: seconds % 60,
    };

    Object.keys(parts).forEach((unit) => {
      const node = this.units[unit];
      if (!node) return;

      const value = unit === 'days' ? String(parts.days) : String(parts[unit]).padStart(2, '0');
      if (node.textContent !== value) node.textContent = value;
    });

    this.announce(parts);
  }

  /**
   * Screen readers should not hear a per-second update. Announce once a minute,
   * in words, from a separate live region.
   */
  announce(parts) {
    if (!this.announcer || parts.minutes === this.lastAnnouncedMinute) return;

    this.lastAnnouncedMinute = parts.minutes;

    const spoken = [
      parts.days ? `${parts.days} days` : null,
      parts.hours ? `${parts.hours} hours` : null,
      `${parts.minutes} minutes`,
    ]
      .filter(Boolean)
      .join(', ');

    this.announcer.textContent = `${spoken} remaining`;
  }

  onExpired() {
    if (this.expired) return;

    this.expired = true;
    this.stop();
    this.setAttribute('expired', '');

    if (this.display) this.display.hidden = true;
    if (this.expiredMessage) this.expiredMessage.hidden = false;
    if (this.announcer) this.announcer.textContent = this.expiredMessage?.textContent.trim() || '';

    this.dispatchEvent(new CustomEvent('sale-countdown:expired', { bubbles: true }));

    if (this.dataset.reloadOnExpiry === 'true') this.reloadPastBoundary();
  }

  /**
   * Doors opening is a server-side change, so the page has to be re-fetched.
   *
   * The cache-buster is the boundary timestamp rather than a random value, so every
   * visitor past the boundary shares one CDN cache entry — and, critically, it is
   * how we avoid reloading forever. If the store did not actually open (the gate is
   * still on, or the merchant hasn't flipped the password switch), the reloaded page
   * shows an already-expired countdown and would reload again. Seeing our own stamp
   * already in the URL means that round-trip has happened, so we stop.
   */
  reloadPastBoundary() {
    const stamp = String(Math.floor(this.target / 1000));
    const url = new URL(window.location.href);

    if (url.searchParams.get('_se') === stamp) return;

    // Belt and braces for the case where the stamp is stripped in transit
    // (a redirect, or an app rewriting query strings).
    const key = `sale-countdown-reloaded:${stamp}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (error) {
      /* Private mode / blocked storage — the URL check above still guards us. */
    }

    url.searchParams.set('_se', stamp);
    window.location.replace(url.toString());
  }
}

if (!customElements.get('sale-countdown')) {
  customElements.define('sale-countdown', SaleCountdown);
}
